/**
 * eligibilityEngine.js — deterministic product elimination.
 *
 * Every rule returns a traceable verdict:
 *   pass        — the product survives this rule
 *   fail        — the product is eliminated (with reason)
 *   conditional — survives with a caution (workbook flag "Conditional")
 *   unknown     — the deciding input is not yet known → needs_data
 *
 * Product status:
 *   ineligible  — at least one rule failed
 *   needs_data  — no failure, but at least one decisive rule is unknown
 *   eligible    — all decisive rules pass (possibly with cautions)
 *
 * IMPORTANT: exact guideline thresholds (FICO floors, LTV ceilings) are NOT
 * in the workbook — it is program-level taxonomy. The guardrails below are
 * widely-published practical floors, used conservatively: a borrower is only
 * eliminated when their entire stated range fails, otherwise the product
 * stays alive with a caution. Lender overlays may differ either way.
 */

/** Practical family credit floors (see note above). */
const FAMILY_CREDIT_FLOOR = {
  conventional: 620,
  fha: 500, // 500–579 requires ~10% down; handled below
  va: 560,
  usda: 580,
  nonqm: 560,
};

/** Practical family max-LTV ceilings used only when the row has no explicit cap. */
const FAMILY_MAX_LTV_DEFAULT = {
  conventional: 97,
  fha: 96.5,
  va: 100,
  usda: 100,
  nonqm: 95,
};

const pass = (rule) => ({ rule, verdict: 'pass' });
const fail = (rule, reason) => ({ rule, verdict: 'fail', reason });
const cond = (rule, caution) => ({ rule, verdict: 'conditional', caution });
const unknown = (rule, inputs, note) => ({ rule, verdict: 'unknown', inputs, note });

/** Evaluate a workbook eligibility flag against a boolean requirement. */
function flagRule(rule, flagValue, reason, caution) {
  if (flagValue === 'yes') return pass(rule);
  if (flagValue === 'conditional') return cond(rule, caution || `${reason} — allowed conditionally; program limits apply`);
  return fail(rule, reason);
}

/**
 * All rule checks for one product. Returns the full trace.
 * @param {import('./borrowerProfile').BorrowerProfile} p profile
 * @param {object} prod normalized catalog product
 */
function runRules(p, prod) {
  const checks = [];
  const { transaction: t, occupancy: o, property: pr, qualification: q, borrower: b, structure: s } = prod;

  // ── Transaction type ──────────────────────────────────────────
  if (p.loanPurpose === undefined) {
    checks.push(unknown('transaction', ['loanPurpose']));
  } else if (p.loanPurpose === 'purchase') {
    if (t.streamline) checks.push(fail('transaction', 'Streamline refinance product — not a purchase option'));
    else checks.push(flagRule('transaction', t.purchase, 'Product does not support purchase'));
  } else {
    // refinance
    if (t.streamline) {
      // Streamline products require a matching existing loan family
      if (p.existingLoanType === undefined) checks.push(unknown('streamline-source', ['existingLoanType']));
      else if (t.streamlineSourceFamily && p.existingLoanType !== t.streamlineSourceFamily) {
        checks.push(fail('streamline-source', `Requires an existing ${t.streamlineSourceFamily.toUpperCase()} loan`));
      } else checks.push(pass('streamline-source'));
      if (p.refiKind === 'cash-out') checks.push(fail('transaction', 'Streamline programs do not allow cash out'));
    } else if (p.refiKind === undefined) {
      checks.push(unknown('transaction', ['refiKind']));
    } else if (p.refiKind === 'cash-out') {
      checks.push(flagRule('transaction', t.cashOutRefi, 'Product does not allow cash-out refinance'));
    } else {
      checks.push(flagRule('transaction', t.rateTermRefi, 'Product does not allow rate/term refinance'));
    }
  }

  // Purpose-specialty products only for borrowers with that need
  if (t.renovation) {
    if (p.renovationNeeded === undefined) checks.push(unknown('renovation', ['renovationNeeded']));
    else if (!p.renovationNeeded) checks.push(fail('renovation', 'Renovation financing product — no renovation need indicated'));
    else checks.push(pass('renovation'));
  } else if (p.renovationNeeded === true) {
    checks.push(fail('renovation', 'Borrower needs renovation financing; product does not include it'));
  }
  if (t.construction) {
    if (p.constructionNeeded !== true) checks.push(fail('construction', 'Construction product — no construction need indicated'));
    else checks.push(pass('construction'));
  }

  // ── Occupancy ─────────────────────────────────────────────────
  if (p.occupancy === undefined) {
    checks.push(unknown('occupancy', ['occupancy']));
  } else {
    const flagValue = p.occupancy === 'primary' ? o.primary : p.occupancy === 'second-home' ? o.secondHome : o.investment;
    checks.push(flagRule('occupancy', flagValue, `Not offered for ${p.occupancy.replace('-', ' ')} occupancy`));
  }

  // DSCR / business-purpose products only make sense for investment property
  if ((q.dscr === 'yes' || prod.businessPurpose) && p.occupancy !== undefined && p.occupancy !== 'investment') {
    checks.push(fail('dscr-occupancy', 'Rental cash-flow (DSCR/business-purpose) products apply to investment properties'));
  }

  // ── Property ──────────────────────────────────────────────────
  if (p.units !== undefined) {
    const [lo, hi] = pr.units;
    if (p.units < lo || p.units > hi) checks.push(fail('units', `Supports ${lo === hi ? lo : `${lo}–${hi}`} unit(s)`));
    else checks.push(pass('units'));
  }
  if (p.propertyType !== undefined) {
    if (p.propertyType === 'condo') checks.push(flagRule('property-type', pr.condo, 'Condos not eligible'));
    else if (p.propertyType === 'coop') checks.push(flagRule('property-type', pr.coop, 'Co-ops not eligible', 'Co-op eligibility is conditional'));
    else if (p.propertyType === 'mixed-use') checks.push(flagRule('property-type', pr.mixedUse, 'Mixed-use not eligible', 'Mixed-use eligibility is conditional'));
    else if (p.propertyType === 'manufactured') {
      checks.push(flagRule('property-type', pr.manufactured, 'Manufactured housing not eligible', 'Manufactured housing allowed with program conditions'));
    } else checks.push(pass('property-type'));
    // MH-specialty programs are only for manufactured homes
    if (pr.manufacturedSpecialty && p.propertyType !== 'manufactured') {
      checks.push(fail('property-type', 'Manufactured-housing program — property is not a manufactured home'));
    }
  }

  // ── Veteran requirement ───────────────────────────────────────
  if (b.veteran.required) {
    if (p.veteranStatus === undefined) checks.push(unknown('veteran', ['veteranStatus']));
    else if (p.veteranStatus !== 'eligible') checks.push(fail('veteran', 'Requires VA-eligible veteran/service member status'));
    else checks.push(pass('veteran'));
    if (b.veteran.existingVaLoan) {
      if (p.existingLoanType === undefined) checks.push(unknown('veteran-existing', ['existingLoanType']));
      else if (p.existingLoanType !== 'va') checks.push(fail('veteran-existing', 'Requires an existing VA loan'));
      else checks.push(pass('veteran-existing'));
    }
  }

  // ── Geography ─────────────────────────────────────────────────
  if (b.geography === 'usda-rural') {
    if (p.ruralEligibility === undefined) checks.push(unknown('geography', ['ruralEligibility']));
    else if (p.ruralEligibility === 'no') checks.push(fail('geography', 'Property is not in a USDA-eligible rural area'));
    else if (p.ruralEligibility === 'unsure') checks.push(cond('geography', 'USDA rural eligibility needs address-level confirmation'));
    else checks.push(pass('geography'));
  } else if (b.geography === 'existing-usda') {
    if (p.existingLoanType === undefined) checks.push(unknown('geography', ['existingLoanType']));
    else if (p.existingLoanType !== 'usda') checks.push(fail('geography', 'Requires an existing USDA loan'));
    else checks.push(pass('geography'));
  } else if (b.geography === 'hfa') {
    checks.push(cond('geography', 'Requires a participating state/local housing finance agency program'));
  } else if (b.geography === 'tribal') {
    if (p.tribalEligible === true) checks.push(pass('geography'));
    else checks.push(fail('geography', 'Limited to eligible tribal areas/land ownership structures'));
  } else if (b.geography === 'disaster') {
    if (p.disasterArea === true) checks.push(pass('geography'));
    else checks.push(fail('geography', 'Limited to presidentially declared disaster areas'));
  }

  // ── First-time homebuyer requirement (e.g. Standard 97, HomeOne) ──
  if (p.loanPurpose === 'purchase' && /first-time homebuyer required/i.test(String(prod.special.feature || ''))) {
    if (p.firstTimeHomebuyer === undefined) checks.push(unknown('first-time', ['firstTimeHomebuyer']));
    else if (!p.firstTimeHomebuyer) checks.push(fail('first-time', 'Requires at least one first-time homebuyer'));
    else checks.push(pass('first-time'));
  }

  // ── Income limits (AMI / USDA household) ─────────────────────
  if (b.incomeLimit.kind === 'ami' || b.incomeLimit.kind === 'hfa') {
    checks.push(unknown('income-limit', ['amiPercent'], 'Area-median-income limit applies — needs address-level AMI check'));
  } else if (b.incomeLimit.kind === 'usda') {
    checks.push(unknown('income-limit', ['householdIncome'], 'USDA household income limit applies'));
  }

  // ── Credit ────────────────────────────────────────────────────
  if (p.creditBand === undefined) {
    checks.push(unknown('credit', ['credit']));
  } else {
    let floor = FAMILY_CREDIT_FLOOR[prod.family] ?? 560;
    // FHA below 580 requires ~10% down
    if (prod.family === 'fha' && p.creditBand.max < 580 && p.ltv !== undefined && p.ltv > 90) floor = 580;
    if (p.creditBand.max < floor) checks.push(fail('credit', `Estimated credit below the practical ${floor}+ range for this program family`));
    else if (p.creditBand.min < floor) checks.push(cond('credit', 'Credit estimate straddles the program floor — exact score will matter'));
    else checks.push(pass('credit'));
    if (p.creditUnsure) checks.push(cond('credit-estimate', 'Credit score was estimated — verify before relying on this match'));
  }

  // ── LTV ───────────────────────────────────────────────────────
  if (p.ltv !== undefined) {
    const cap = q.maxLtvParsed ?? FAMILY_MAX_LTV_DEFAULT[prod.family] ?? 97;
    if (p.ltv > cap + 0.01) {
      checks.push(fail('ltv', `LTV ${p.ltv.toFixed(1)}% exceeds the ${cap}% guide for this product`));
    } else {
      checks.push(pass('ltv'));
      if (prod.family === 'conventional' && p.ltv > 80) checks.push(cond('mi', 'Mortgage insurance likely applies above 80% LTV'));
    }
  } else if (p.loanPurpose !== undefined) {
    checks.push(unknown('ltv', p.loanPurpose === 'purchase' ? ['value', 'down'] : ['value', 'down']));
  }

  // ── Loan size vs limits ───────────────────────────────────────
  if (p.loanAmount !== undefined) {
    const limit = p.conformingLimit || (prod.economics.baseline1UnitLimit ?? 806500);
    const kind = prod.economics.loanLimitKind;
    if (kind === 'conforming' && p.loanAmount > limit) {
      checks.push(fail('loan-size', `Loan amount exceeds the area conforming limit ($${limit.toLocaleString()})`));
    } else if (kind === 'high-balance') {
      const baseline = prod.economics.baseline1UnitLimit ?? 806500;
      if (p.loanAmount <= baseline) checks.push(fail('loan-size', 'High-balance product — loan is within the standard conforming limit'));
      else if (p.loanAmount > limit) checks.push(fail('loan-size', `Loan amount exceeds the area high-balance limit ($${limit.toLocaleString()})`));
      else checks.push(pass('loan-size'));
    } else if (kind === 'jumbo') {
      if (p.loanAmount <= limit) checks.push(fail('loan-size', 'Jumbo product — loan fits within conforming limits'));
      else checks.push(pass('loan-size'));
    } else if (kind === 'fha' && p.loanAmount > 650000) {
      checks.push(cond('loan-size', 'Verify the FHA county loan limit for this loan size'));
    } else {
      checks.push(pass('loan-size'));
    }
  }

  // ── Citizenship-specialty products ───────────────────────────
  if (b.citizenship === 'foreign-national') {
    if (p.citizenship === undefined) checks.push(unknown('citizenship', ['citizenship'], 'Foreign-national program'));
    else if (p.citizenship !== 'foreign-national') checks.push(fail('citizenship', 'Foreign-national program — borrower has U.S. residency status'));
    else checks.push(pass('citizenship'));
  } else if (b.citizenship === 'itin') {
    if (p.citizenship === undefined) checks.push(unknown('citizenship', ['citizenship'], 'ITIN program'));
    else if (p.citizenship !== 'itin') checks.push(fail('citizenship', 'ITIN program — borrower has an SSN'));
    else checks.push(pass('citizenship'));
  }

  // ── Documentation path ────────────────────────────────────────
  if (q.altDoc) {
    // Alt-doc products exist to solve a documentation problem
    if (p.incomeType === undefined) checks.push(unknown('income-doc', ['employment']));
    else if (p.incomeType === 'w2') checks.push(fail('income-doc', 'Alt-doc program — W-2 income documents normally, standard programs apply'));
    else if (p.incomeType === 'retired' && q.altDocKind !== 'asset') checks.push(fail('income-doc', 'Alt-doc program aimed at self-employment income'));
    else checks.push(pass('income-doc'));
  }
  if (q.dscrOnly && p.occupancy === undefined) checks.push(unknown('dscr-occupancy', ['occupancy']));

  // ── Age (reverse products are outside this universe, kept for safety) ──
  if (b.minAge != null) {
    if (p.borrowerAge === undefined) checks.push(unknown('age', ['borrowerAge']));
    else if (p.borrowerAge < b.minAge) checks.push(fail('age', `Requires borrower age ${b.minAge}+`));
    else checks.push(pass('age'));
  }

  return checks;
}

/**
 * Evaluate the borrower against every product in the universe.
 * @returns {Array<{product, status, failedRules, missingInputs, cautions, reasons, checks}>}
 */
export function evaluateEligibility(profile, products) {
  return products.map((product) => {
    const checks = runRules(profile, product);
    const failed = checks.filter((c) => c.verdict === 'fail');
    const unknowns = checks.filter((c) => c.verdict === 'unknown');
    const conditionals = checks.filter((c) => c.verdict === 'conditional');

    let status;
    if (failed.length) status = 'ineligible';
    else if (unknowns.length) status = 'needs_data';
    else status = 'eligible';

    return {
      product,
      status,
      failedRules: failed.map((c) => ({ rule: c.rule, reason: c.reason })),
      missingInputs: [...new Set(unknowns.flatMap((c) => c.inputs))],
      cautions: conditionals.map((c) => c.caution).filter(Boolean),
      reasons: checks.filter((c) => c.verdict === 'pass').map((c) => c.rule),
      checks,
    };
  });
}

export { FAMILY_CREDIT_FLOOR, FAMILY_MAX_LTV_DEFAULT };
